/**
 * AnnotationCanvasComponent
 * Angular SVG clone of Magic Patterns media/CanvasOverlay.tsx.
 *
 * Renders annotations as SVG over the media and handles drawing/selection/drag.
 * Annotations are stored in PERCENT coords; the SVG uses a viewBox matching the
 * element's pixel size (so circles/text don't distort under preserveAspectRatio).
 */
import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
  HostListener, Input, Output, ViewChild
} from '@angular/core';
import { Annotation, AnnotationType, DrawingTool, Point } from './media-annotation.types';

let annId = 0;

@Component({
  standalone: false,
  selector: 'app-doc-annotation-canvas',
  templateUrl: './doc-annotation-canvas.component.html',
  styleUrls: ['./doc-annotation-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocAnnotationCanvasComponent implements AfterViewInit {
  @Input() annotations: Annotation[] = [];
  @Input() activeTool: DrawingTool = 'select';
  @Input() color = '#FF6B35';
  @Input() strokeWidth = 4;
  @Input() opacity = 1;
  @Input() selectedId: string | null = null;
  @Input() showAnnotations = true;

  @Output() addAnnotation = new EventEmitter<Annotation>();
  @Output() updateAnnotation = new EventEmitter<{ id: string; changes: Partial<Annotation> }>();
  @Output() selectAnnotation = new EventEmitter<string | null>();
  /** Emitted when the select tool clicks empty space (used by the host to place comments). */
  @Output() backgroundClick = new EventEmitter<Point>();
  @Output() textRequested = new EventEmitter<Point>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  vw = 1;
  vh = 1;

  draft: Annotation | null = null;
  private isDrawing = false;
  private isDragging = false;
  private dragStart: Point = { x: 0, y: 0 };
  private dragOrigin: Annotation | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.updateSize();
  }

  // Re-measure the SVG's live pixel size → drives the percent→pixel mapping.
  // Called on window resize AND by the host once the image finishes loading
  // (in a dialog the image lays out AFTER ngAfterViewInit, so the first measure
  // can be stale; without a re-measure, fixed-size marks like text render at the
  // wrong scale/position). markForCheck() repaints under OnPush when called
  // outside an Angular event.
  @HostListener('window:resize')
  updateSize(): void {
    const r = this.svgRef.nativeElement.getBoundingClientRect();
    if (r.width && r.height) { this.vw = r.width; this.vh = r.height; }
    this.cdr.markForCheck();
  }

  // ---------- coordinate helpers ----------
  private toPercent(e: MouseEvent): Point {
    const r = this.svgRef.nativeElement.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  }
  pxX(v: number): number { return (v / 100) * this.vw; }
  pxY(v: number): number { return (v / 100) * this.vh; }

  get cursorClass(): string {
    return this.activeTool === 'select' ? 'is-select' : 'is-draw';
  }

  get selected(): Annotation | null {
    return this.annotations.find(a => a.id === this.selectedId) || null;
  }

  // ---------- rendering helpers ----------
  penPoints(a: Annotation): string {
    return (a.points || []).map(p => `${this.pxX(p.x)},${this.pxY(p.y)}`).join(' ');
  }
  radiusPx(a: Annotation): number {
    if (!a.start || !a.end) return 0;
    const dx = this.pxX(a.end.x) - this.pxX(a.start.x);
    const dy = this.pxY(a.end.y) - this.pxY(a.start.y);
    return Math.sqrt(dx * dx + dy * dy);
  }
  rectX(a: Annotation): number { return Math.min(this.pxX(a.start!.x), this.pxX(a.end!.x)); }
  rectY(a: Annotation): number { return Math.min(this.pxY(a.start!.y), this.pxY(a.end!.y)); }
  rectW(a: Annotation): number { return Math.abs(this.pxX(a.end!.x) - this.pxX(a.start!.x)); }
  rectH(a: Annotation): number { return Math.abs(this.pxY(a.end!.y) - this.pxY(a.start!.y)); }
  midX(a: Annotation): number { return (this.pxX(a.start!.x) + this.pxX(a.end!.x)) / 2; }
  midY(a: Annotation): number { return (this.pxY(a.start!.y) + this.pxY(a.end!.y)) / 2; }

  /** Bounds (px) for the selection box. */
  bounds(a: Annotation): { x: number; y: number; w: number; h: number } {
    if (a.points && a.points.length) {
      const xs = a.points.map(p => this.pxX(p.x)); const ys = a.points.map(p => this.pxY(p.y));
      const x = Math.min(...xs), y = Math.min(...ys);
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }
    if (a.start && a.end) {
      if (a.type === 'circle') {
        const r = this.radiusPx(a);
        return { x: this.pxX(a.start.x) - r, y: this.pxY(a.start.y) - r, w: r * 2, h: r * 2 };
      }
      return { x: this.rectX(a), y: this.rectY(a), w: this.rectW(a), h: this.rectH(a) };
    }
    if (a.start) {
      const fs = a.fontSize || 16;
      return { x: this.pxX(a.start.x), y: this.pxY(a.start.y) - fs, w: (a.text?.length || 4) * fs * 0.6, h: fs * 1.3 };
    }
    return { x: 0, y: 0, w: 0, h: 0 };
  }

  measureLabel(a: Annotation): string {
    return `${(a.measurementValue || 0).toFixed(2)}${a.measurementUnit || 'm'}`;
  }

  /** End-tick endpoints (px) for a ruler annotation, matching MP's perpendicular ticks. */
  rulerTick(a: Annotation, end: 'start' | 'end', side: 1 | -1): { x: number; y: number } {
    const sx = this.pxX(a.start!.x), sy = this.pxY(a.start!.y);
    const ex = this.pxX(a.end!.x), ey = this.pxY(a.end!.y);
    const angle = Math.atan2(ey - sy, ex - sx) + side * (Math.PI / 2);
    const ox = end === 'start' ? sx : ex;
    const oy = end === 'start' ? sy : ey;
    const t = 10; // tick length (px), matches MP tickSize
    return { x: ox + t * Math.cos(angle), y: oy + t * Math.sin(angle) };
  }

  // ---------- interaction ----------
  onMouseDown(e: MouseEvent): void {
    const pt = this.toPercent(e);

    if (this.activeTool === 'select') {
      const hit = this.hitTest(pt);
      this.selectAnnotation.emit(hit ? hit.id : null);
      if (hit && !hit.locked) {
        this.isDragging = true;
        this.dragStart = pt;
        this.dragOrigin = JSON.parse(JSON.stringify(hit));
      } else if (!hit) {
        this.backgroundClick.emit(pt);
      }
      return;
    }

    if (this.activeTool === 'text') {
      // Let the host collect the text with an in-canvas popup (no browser prompt()).
      this.textRequested.emit(pt);
      return;
    }

    // start drawing
    this.isDrawing = true;
    const typeMap: Record<string, AnnotationType> = {
      pen: 'draw', arrow: 'arrow', rectangle: 'rectangle', circle: 'circle', ruler: 'ruler', area: 'area'
    };
    const type = typeMap[this.activeTool] || 'draw';
    this.draft = this.base(type, this.activeTool === 'pen' ? { points: [pt] } : { start: pt, end: pt });
  }

  onMouseMove(e: MouseEvent): void {
    if (this.isDragging && this.dragOrigin) {
      const pt = this.toPercent(e);
      const dx = pt.x - this.dragStart.x, dy = pt.y - this.dragStart.y;
      const o = this.dragOrigin;
      const changes: Partial<Annotation> = {};
      if (o.points) changes.points = o.points.map(p => ({ x: p.x + dx, y: p.y + dy }));
      if (o.start) changes.start = { x: o.start.x + dx, y: o.start.y + dy };
      if (o.end) changes.end = { x: o.end.x + dx, y: o.end.y + dy };
      this.updateAnnotation.emit({ id: o.id, changes });
      return;
    }
    if (this.isDrawing && this.draft) {
      const pt = this.toPercent(e);
      if (this.draft.type === 'draw') {
        this.draft = { ...this.draft, points: [...(this.draft.points || []), pt] };
      } else {
        this.draft = { ...this.draft, end: pt };
        if (this.draft.type === 'ruler' || this.draft.type === 'area') {
          this.draft.measurementValue = this.measure(this.draft);
          this.draft.measurementUnit = this.draft.type === 'area' ? 'm²' : 'm';
        }
      }
    }
  }

  onMouseUp(): void {
    if (this.isDragging) { this.isDragging = false; this.dragOrigin = null; return; }
    if (this.isDrawing && this.draft) {
      const a = this.draft;
      const valid = (a.type === 'draw' && (a.points?.length || 0) > 1) ||
        (!!a.start && !!a.end && (a.start.x !== a.end.x || a.start.y !== a.end.y));
      if (valid) this.addAnnotation.emit(a);
    }
    this.isDrawing = false;
    this.draft = null;
  }

  /**
   * Single, consistent calibration: 100 displayed px = 1 unit (matches MP CanvasOverlay).
   * Percent coords are converted to px via the live viewBox so ruler (m) and area (m²)
   * share the same px↔unit factor regardless of stage zoom.
   */
  private readonly PX_PER_UNIT = 100;
  private measure(a: Annotation): number {
    const dx = this.pxX(a.end!.x) - this.pxX(a.start!.x);
    const dy = this.pxY(a.end!.y) - this.pxY(a.start!.y);
    const distUnits = Math.sqrt(dx * dx + dy * dy) / this.PX_PER_UNIT;
    return a.type === 'area' ? distUnits * distUnits : distUnits;
  }

  private base(type: AnnotationType, extra: Partial<Annotation>): Annotation {
    return {
      id: `ann-${Date.now()}-${annId++}`,
      type,
      color: this.color,
      strokeWidth: this.strokeWidth,
      opacity: this.opacity,
      author: 'You',
      timestamp: new Date().toISOString(),
      ...extra
    };
  }

  private hitTest(pt: Point): Annotation | null {
    for (let i = this.annotations.length - 1; i >= 0; i--) {
      const a = this.annotations[i];
      const b = this.bounds(a);
      const px = this.pxX(pt.x), py = this.pxY(pt.y);
      const pad = 6;
      if (px >= b.x - pad && px <= b.x + b.w + pad && py >= b.y - pad && py <= b.y + b.h + pad) {
        return a;
      }
    }
    return null;
  }

  trackById(_: number, a: Annotation): string { return a.id; }
}
